import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ScrollToTop from './components/ScrollToTop';
import PwaInstallPrompt from './components/PwaInstallPrompt';

// Public Pages
import HomePage from './pages/HomePage';
import SearchPage from './pages/SearchPage';
import DoctorProfilePage from './pages/DoctorProfilePage';
import DoctorSlugResolver from './pages/DoctorSlugResolver';
import ClinicProfilePage from './pages/ClinicProfilePage';
import SpecialtyCityPage from './pages/SpecialtyCityPage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import RegisterPage from './pages/RegisterPage';
import BookingPage from './pages/BookingPage';

import ComoFuncionaPJPage from './pages/ComoFuncionaPJPage';

// Patient Pages
import PatientDashboard from './pages/patient/PatientDashboard';
import PatientAppointments from './pages/patient/PatientAppointments';
import PatientProfile from './pages/patient/PatientProfile';
import PatientWaitlist from './pages/patient/PatientWaitlist';

// Provider Pages
import ProviderDoctors from './pages/provider/ProviderDoctors';
import ProviderSchedules from './pages/provider/ProviderSchedules';
import ProviderAppointments from './pages/provider/ProviderAppointments';
import ProviderProfile from './pages/provider/ProviderProfile';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminProviders from './pages/admin/AdminProviders';
import AdminDoctors from './pages/admin/AdminDoctors';
import AdminSpecialties from './pages/admin/AdminSpecialties';
import AdminHealthPlans from './pages/admin/AdminHealthPlans';
import AdminAppointments from './pages/admin/AdminAppointments';
import AdminReviews from './pages/admin/AdminReviews';
import AdminClaims from './pages/admin/AdminClaims';
import AdminBackups from './pages/admin/AdminBackups';
import AdminDoctorsByHealthPlan from './pages/admin/AdminDoctorsByHealthPlan';
import AdminImportedDoctors from './pages/admin/AdminImportedDoctors';
import AdminSearchAnalytics from './pages/admin/AdminSearchAnalytics';
import AdminCities from './pages/admin/AdminCities';
import AdminScraperCoverage from './pages/admin/AdminScraperCoverage';
import AdminSpecialtyMerge from './pages/admin/AdminSpecialtyMerge';
import AboutPage from './pages/AboutPage';
import BlogPage from './pages/BlogPage';
import BlogArticlePage from './pages/BlogArticlePage';
import CompliancePage from './pages/CompliancePage';
import EcosystemPage from './pages/EcosystemPage';
import TermsPage from './pages/TermsPage';
import ClaimPage from './pages/ClaimPage';
import ChangePasswordPage from './pages/ChangePasswordPage';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/sobre" element={<AboutPage />} />
      <Route path="/buscar" element={<SearchPage />} />
      <Route path="/especialistas/:specialtySlug/:cityUf" element={<SpecialtyCityPage />} />
      <Route path="/medico/:id" element={<DoctorProfilePage />} />
      <Route path="/clinica/:id" element={<ClinicProfilePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/esqueci-senha" element={<ForgotPasswordPage />} />
      <Route path="/redefinir-senha" element={<ResetPasswordPage />} />
      <Route path="/cadastro" element={<RegisterPage />} />
      <Route path="/agendar/:doctorId" element={<BookingPage />} />
      <Route path="/como-funciona-pj" element={<ComoFuncionaPJPage />} />
      <Route path="/seguranca" element={<CompliancePage />} />
      <Route path="/ecosistema" element={<EcosystemPage />} />
      <Route path="/ecossistema" element={<EcosystemPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogArticlePage />} />
      <Route path="/termos" element={<TermsPage />} />
      <Route path="/claim" element={<ClaimPage />} />
      <Route path="/conta/senha" element={<ChangePasswordPage />} />

      {/* Patient Routes */}
      <Route
        path="/paciente"
        element={
          <ProtectedRoute allowedRoles={['patient']}>
            <PatientDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/paciente/consultas"
        element={
          <ProtectedRoute allowedRoles={['patient']}>
            <PatientAppointments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/paciente/perfil"
        element={
          <ProtectedRoute allowedRoles={['patient']}>
            <PatientProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/paciente/lista-espera"
        element={
          <ProtectedRoute allowedRoles={['patient']}>
            <PatientWaitlist />
          </ProtectedRoute>
        }
      />

      {/* Provider Routes */}
      <Route
        path="/perfil-medico"
        element={
          <ProtectedRoute allowedRoles={['provider']}>
            <Navigate to="/prestador/perfil" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/prestador"
        element={
          <ProtectedRoute allowedRoles={['provider']}>
            <Navigate to="/prestador/perfil" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/prestador/medicos"
        element={
          <ProtectedRoute allowedRoles={['provider']}>
            <ProviderDoctors />
          </ProtectedRoute>
        }
      />
      <Route
        path="/prestador/agendas"
        element={
          <ProtectedRoute allowedRoles={['provider']}>
            <ProviderSchedules />
          </ProtectedRoute>
        }
      />
      <Route
        path="/prestador/consultas"
        element={
          <ProtectedRoute allowedRoles={['provider']}>
            <ProviderAppointments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/prestador/perfil"
        element={
          <ProtectedRoute allowedRoles={['provider']}>
            <ProviderProfile />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/usuarios"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminUsers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/prestadores"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminProviders />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/medicos"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDoctors />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/medicos-credenciados"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDoctorsByHealthPlan />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/especialidades"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminSpecialties />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/convenios"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminHealthPlans />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/consultas"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminAppointments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/avaliacoes"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminReviews />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/reivindicacoes"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminClaims />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/backups"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminBackups />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/importados"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminImportedDoctors />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/inteligencia"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminSearchAnalytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/cidades"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminCities />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/cobertura-scraper"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminScraperCoverage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/especialidades/mesclar"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminSpecialtyMerge />
          </ProtectedRoute>
        }
      />

      {/* Fallback: try doctor slug, then 404 */}
      <Route path="/:slug" element={<DoctorSlugResolver />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <AppRoutes />
        <PwaInstallPrompt />
        <Toaster 
          position="top-right" 
          richColors 
          closeButton
          toastOptions={{
            style: {
              fontFamily: 'Manrope, sans-serif',
            },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
